import { Component } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import fileDownload from 'js-file-download';

import JSZip from 'jszip';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.css']
})

export class FileUploadComponent {
  private backendUri: string = environment.backendUrl + '/api/values';
  public files: any = [];
  public projectFileExtension = 'sdp';
  public isAvbAccepted = false;
  public isUploading = false;
  public invalidData = false;
  public erroneousResponse = false;
  public errorMessage = '';
  public showProgressBar = false;
  public currentFileName = '';
  public fileAmount = 0;
  public currentFileCount = 0;
  private readonly maxNumberOfFiles = 10;
  private downloadZip = false;
  public errorFiles = [];
  public show_success_msg = false;
  public success_msg = '';
  public successfull = true;

  /**
   * Restricted characters for filename validation
   */
  private readonly restrictedFilenameChars = [
    // Known WAF Issues  (PMS Azure instance issue (STT-4872))
    '=', "'", ';',
    // Path Traversal & Filesystem
    '/', '\\', ':',
    // Windows reserved / Security-relevant Characters
    '<', '>', '"', '`', '|', '?', '*', '\0'
  ];

  constructor(private http: HttpClient) { }

  /**
   * Extracts filename from Content-Disposition header (RFC 6266)
   *
   * @param response HTTP response with headers
   * @returns Extracted filename or null if not found
   */
  private extractFilenameFromResponse(response: any): string | null {
    const contentDisposition =
      response.headers.get('content-disposition') ||
      response.headers.get('Content-Disposition');

    if (!contentDisposition) {
      return null;
    }

    // Try filename*= first (RFC 5987 - supports UTF-8)
    // Backend sends: filename*=utf-8''encoded-name
    const filenameStarRegex = /filename\*=utf-8''([^;\n]+)/i;
    const starMatch = filenameStarRegex.exec(contentDisposition);
    if (starMatch?.[1]) {
      try {
        return decodeURIComponent(starMatch[1]);
      } catch (e) {
      }
    }

    // Fallback to filename= (standard)
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const match = filenameRegex.exec(contentDisposition);
    if (match?.[1]) {
      return match[1].replace(/['"]/g, '').trim();
    }

    return null;
  }

  /**
   * Generates fallback filename by changing extension
   *
   * @param originalName Original filename (e.g., "project.sdp")
   * @param newExtension New extension (e.g., ".sdp2")
   * @returns Filename with new extension (e.g., "project.sdp2")
   */
  private changeFileExtension(originalName: string, newExtension: string): string {
    const lastDotIndex = originalName.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return originalName + newExtension;
    }
    const baseName = originalName.substring(0, lastDotIndex);
    return baseName + (newExtension.startsWith('.') ? newExtension : '.' + newExtension);
  }

  public onInputFileChanged(event: any): void {
    this.initializeForm();
    this.files = [];

    for (const file of event.target.files) {
      const validationResult = this.validateFileNameForUX(file.name);
      if (!validationResult.isValid) {
        this.invalidData = true;
        this.errorMessage = validationResult.errorMessage;
        return;
      }

      if (this.hasSupportedFileExtension(file.name)) {
        this.files.push(file);
      }
    }

    this.validateFileCount();
  }

  /**
   * Lightweight frontend validation (Early-fail feedback)
   *
   * @param fileName The filename to validate
   * @returns Validation result with isValid flag and error message
   */
  private validateFileNameForUX(fileName: string): { isValid: boolean; errorMessage: string } {
    // Check 1: Empty filename
    if (!fileName || fileName.trim().length === 0) {
      return {
        isValid: false,
        errorMessage: 'Please select a file.'
      };
    }

    const nameWithoutExtension = fileName.substring(0, fileName.lastIndexOf('.')) || fileName;

    // Check 2: Control characters (ASCII 0-31, 127)
    // Includes: \0 (null byte), \n, \r, \t, etc.
    // eslint-disable-next-line no-control-regex
    const controlCharRegex = /[\x00-\x1F\x7F]/;
    if (controlCharRegex.test(fileName)) {
      return {
        isValid: false,
        errorMessage:
          'The filename contains invalid characters. ' +
          'Please use only letters, numbers, periods (.), hyphens (-), or underscores (_).'
      };
    }

    // Check 3: Path traversal (..)
    // Check before other characters to catch ../ or ..\ patterns
    if (fileName.includes('..')) {
      return {
        isValid: false,
        errorMessage:
          'The filename contains invalid characters. ' +
          'Please use only letters, numbers, periods (.), hyphens (-), or underscores (_).'
      };
    }

    // Check 4: Restricted characters
    const foundRestrictedChars = this.restrictedFilenameChars.filter(
      char => nameWithoutExtension.includes(char)
    );

    if (foundRestrictedChars.length > 0) {
      return {
        isValid: false,
        errorMessage:
          'Special characters aren\'t allowed in the filename. ' +
          'Please use only letters, numbers, periods (.), hyphens (-), or underscores (_).'
      };
    }

    return { isValid: true, errorMessage: '' };
  }

  public uploadButtonClick(): void {
    this.validateFileCount();
    this.initializeUploading();
    this.uploadFiles();
  }

  public onKeyUp(event: KeyboardEvent) {
    if (event.key !== 'Enter') {
      return;
    }

    this.uploadButtonClick();
  }

  private validateFileCount() {
    if (this.files.length > this.maxNumberOfFiles) {
      this.errorMessage = 'Too many files selected. You can upload up to ' + this.maxNumberOfFiles + ' files.';
      this.invalidData = true;
      this.tearDownUploading();
    } else if (this.files.length <= 0) {
      this.tearDownUploading();
    }
  }

  private async uploadFiles() {
    const processedFiles: File[] = [];
    this.currentFileCount = 0;
    this.currentFileName = '';
    this.errorFiles = [];
    this.show_success_msg = false;
    this.fileAmount = this.files.length;
    this.successfull = true;

    for (const file of this.files) {
      this.currentFileName = file.name;
      this.currentFileCount = this.currentFileCount + 1;
      try {
        this.showProgressBar = true;
        const blobFromFile = new Blob([file]);

        // set Content-Type of file explicitly independent of local os file app registrations
        const customFile = new File([blobFromFile], file.name, {
          type: 'application/octet-stream'
        });

        const formData = new FormData();
        formData.append('file', customFile, file.name);

        const response = await this.http.post(this.backendUri, formData, {
          responseType: 'blob',
          observe: 'response'
        }).toPromise();

        // Extract ALL relevant information from backend response headers
        // 1. Get filename from Content-Disposition header
        let fileName = this.extractFilenameFromResponse(response);

        if (!fileName) {
          fileName = this.changeFileExtension(file.name, '.sdp2');
        } else {
        }

        // 2. Get Content-Type from backend response header
        const contentType = response.headers.get('content-type') || 'application/octet-stream';
        const content = response.body;

        // Create File with backend-provided metadata (filename + content-type)
        const processedFile = new File([content], fileName, { type: contentType });
        processedFiles.push(processedFile);

        this.downloadZip = true;

        if (this.fileAmount <= 1) {
          fileDownload(content, fileName);
          this.downloadZip = false;
        }

        this.success_msg = 'All files successfully converted';

      } catch (error) {
        this.erroneousResponse = true;
        this.errorFiles.push(this.currentFileName)
        try {
          if (processedFiles.length < this.files.length) {
            if (this.errorFiles.length > 1) {
              this.errorMessage = 'Could not convert all project files. ' +
                this.errorFiles.length + ' files could not be converted: '
            } else {
              this.errorMessage = 'Could not convert all project files. ' +
                this.errorFiles.length + ' file could not be converted: '
            }

            this.successfull = false;
          }
        } catch {
          this.errorMessage = 'Could not convert project file. ' +
            'Received error while connecting to the converting service. ' +
            'Please try again.';
        }
        this.tearDownUploading();
      }

    }
    if (this.downloadZip == true) {
      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '') + '_' + new Date().toTimeString().slice(0, 5).replace(':', '');
      const zipFileName = `converted_files_${timestamp}.zip`;
      await this.createZipAndDownload(processedFiles, zipFileName);
      this.downloadZip = false;
    }
    this.showProgressBar = false;
    this.tearDownUploading();
    if (this.successfull == true) {
      this.show_success_msg = true;
      this.success_msg = 'All files successfully converted';
    }
  }

  private async createZipAndDownload(files: File[], zipFileName: string) {
    const zip = new JSZip();
    const folder = zip.folder("converted_files");

    files.forEach(file => {
      folder.file(file.name, file);
    });

    const zipContent = await zip.generateAsync({ type: "blob" });
    saveAs(zipContent, zipFileName);
  }

  private hasSupportedFileExtension(fileName: any): boolean {
    const extension = fileName.slice((Math.max(0, fileName.lastIndexOf('.')) || Infinity) + 1);
    if (extension !== this.projectFileExtension) {
      this.invalidData = true;
      this.errorMessage = `Wrong file format: Expected .${this.projectFileExtension} but received .${extension}`;
      return false;
    }
    return true;
  }

  private initializeForm() {
    this.invalidData = false;
    this.erroneousResponse = false;
  }

  private initializeUploading() {
    this.isUploading = true;
    this.initializeForm();
  }

  private tearDownUploading() {
    this.isUploading = false;
  }
}
